import { useCallback, useEffect, useRef, useState } from 'react';
import { CreditRequestPhase, CreditRequestState } from '../../hooks/useCreditRequest';
import { REQUESTED_MESSAGE, requestFailedMessage } from '../../utils/creditRequest';

/**
 * Design-gallery stand-ins for `useCreditRequest`: the same four states the
 * live control moves through, with no server behind them. `request` here only
 * plays the transition, so a click in the gallery shows idle → requesting →
 * just requested exactly as the real one does.
 */

const noop = () => Promise.resolve();

/** A fixed state, for the side-by-side rows. */
export const fixedRequestState = (phase: CreditRequestPhase): CreditRequestState => ({
  phase,
  requestedOn: phase === 'pending' || phase === 'requested' ? '3 Oct' : undefined,
  declinedOn: phase === 'declined' ? '3 Oct' : undefined,
  askAgainAt: phase === 'declined' ? '4 Oct, 14:30' : undefined,
  message:
    phase === 'requested' ? REQUESTED_MESSAGE : phase === 'failed' ? requestFailedMessage('our team could not be reached') : undefined,
  request: noop,
});

/** The live control's journey, played locally: idle, requesting, then just requested. */
export const useDemoCreditRequest = (): CreditRequestState => {
  const [phase, setPhase] = useState<CreditRequestPhase>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const request = useCallback(async () => {
    setPhase('requesting');
    timer.current = setTimeout(() => setPhase('requested'), 700);
  }, []);

  return { ...fixedRequestState(phase), request };
};

export const REQUEST_PHASES: Array<{ label: string; phase: CreditRequestPhase }> = [
  { label: 'Idle — the button', phase: 'idle' },
  { label: 'Pending — waiting for our team', phase: 'pending' },
  { label: 'Just requested', phase: 'requested' },
  { label: 'Declined — waiting to ask again', phase: 'declined' },
  { label: 'Could not send', phase: 'failed' },
];
